import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// File: /home/ubuntu/avaai_dashboard/frontend/src/pages/Support.tsx
import { useEffect, useState } from "react";
import { getAuthToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";
function getStoredUser() {
    const keys = ["ava_user", "user", "ava_profile", "profile", "user_profile"];
    for (const key of keys) {
        try {
            const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
            if (!raw)
                continue;
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object")
                return parsed;
        }
        catch {
            // ignore malformed storage
        }
    }
    return {};
}
function getStoredName(user) {
    return user.display_name || user.name || user.full_name || user.username || "";
}
function getStoredEmail(user) {
    return user.email || "";
}
export default function Support() {
    const navigate = useNavigate();
    const storedUser = getStoredUser();
    const [name, setName] = useState(getStoredName(storedUser));
    const [email, setEmail] = useState(getStoredEmail(storedUser));
    const [category, setCategory] = useState("general");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [includeAccountContext, setIncludeAccountContext] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [errorText, setErrorText] = useState("");
    const token = getAuthToken() || "";
    const [isMobileSupport, setIsMobileSupport] = useState(() => {
        if (typeof window === "undefined")
            return false;
        return window.innerWidth <= 760;
    });
    useEffect(() => {
        const syncMobileSupport = () => setIsMobileSupport(window.innerWidth <= 760);
        syncMobileSupport();
        window.addEventListener("resize", syncMobileSupport);
        return () => window.removeEventListener("resize", syncMobileSupport);
    }, []);
    useEffect(() => {
        document.title = "AVAAIO™ Support";
    }, []);
    const apiBase = (import.meta.env?.VITE_API_URL ||
        import.meta.env?.VITE_API_BASE ||
        "https://api.avaaio.com").replace(/\/$/, "");
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorText("");
        setSubmitted(false);
        if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
            setErrorText("Please complete all required fields.");
            return;
        }
        try {
            setSending(true);
            const payload = {
                name: name.trim(),
                email: email.trim(),
                category,
                subject: subject.trim(),
                message: message.trim(),
                include_account_context: includeAccountContext,
                session_context: includeAccountContext
                    ? {
                        has_token: !!token,
                        user_agent: navigator.userAgent,
                        page: window.location.href,
                    }
                    : null,
            };
            const res = await fetch(`${apiBase}/support`, { credentials: "include",
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) {
                throw new Error(data?.message || "Failed to send support request.");
            }
            setSubmitted(true);
            setSubject("");
            setMessage("");
        }
        catch (err) {
            setErrorText(err?.message || "Unable to send support request right now.");
        }
        finally {
            setSending(false);
        }
    };
    const shellStyle = {
        minHeight: "100vh",
        background: "radial-gradient(circle at top, rgba(0,255,255,0.10), transparent 30%), linear-gradient(180deg, #040914 0%, #07111d 45%, #03070f 100%)",
        color: "#eafcff",
        fontFamily: "Orbitron, sans-serif",
        padding: isMobileSupport ? "18px 12px 110px" : "28px 16px 90px",
        overflowX: "hidden",
        boxSizing: "border-box",
    };
    const pageStyle = {
        maxWidth: 1280,
        width: "100%",
        margin: "0 auto",
        minWidth: 0,
    };
    const topGrid = {
        display: "grid",
        gridTemplateColumns: isMobileSupport ? "1fr" : "1.2fr 0.9fr",
        gap: isMobileSupport ? 12 : 16,
        alignItems: "stretch",
        marginBottom: 16,
    };
    const heroStyle = {
        border: "1px solid rgba(0,255,255,0.18)",
        borderRadius: isMobileSupport ? 20 : 28,
        padding: isMobileSupport ? "18px 14px" : "24px 24px 22px",
        background: "rgba(7, 14, 28, 0.84)",
        boxShadow: "0 0 30px rgba(0,255,255,0.08)",
        backdropFilter: "blur(10px)",
    };
    const sideStyle = {
        border: "1px solid rgba(0,255,255,0.18)",
        borderRadius: isMobileSupport ? 20 : 28,
        padding: isMobileSupport ? "18px 14px" : "24px 24px 22px",
        background: "rgba(7, 14, 28, 0.84)",
        boxShadow: "0 0 30px rgba(0,255,255,0.08)",
        backdropFilter: "blur(10px)",
    };
    const bodyGrid = {
        display: "grid",
        gridTemplateColumns: isMobileSupport ? "1fr" : "1.1fr 0.9fr",
        gap: isMobileSupport ? 12 : 16,
        alignItems: "start",
    };
    const formCard = {
        border: "1px solid rgba(0,255,255,0.18)",
        borderRadius: isMobileSupport ? 20 : 24,
        padding: isMobileSupport ? 16 : 22,
        background: "rgba(7, 14, 28, 0.84)",
        boxShadow: "0 0 24px rgba(0,255,255,0.07)",
    };
    const infoCard = {
        border: "1px solid rgba(0,255,255,0.18)",
        borderRadius: isMobileSupport ? 20 : 24,
        padding: isMobileSupport ? 16 : 22,
        background: "rgba(7, 14, 28, 0.84)",
        boxShadow: "0 0 24px rgba(0,255,255,0.07)",
    };
    const titleStyle = {
        margin: 0,
        fontSize: isMobileSupport ? "clamp(1.75rem, 9vw, 2.45rem)" : "clamp(2.2rem, 4vw, 4rem)",
        lineHeight: 1,
        color: "#20e7ff",
        letterSpacing: 1,
    };
    const eyebrowStyle = {
        color: "#6cf3ff",
        fontSize: 12,
        letterSpacing: 2,
        textTransform: "uppercase",
        opacity: 0.9,
        marginBottom: 10,
    };
    const subStyle = {
        marginTop: 14,
        maxWidth: 760,
        fontSize: isMobileSupport ? 14 : 15,
        lineHeight: 1.6,
        color: "rgba(234,252,255,0.86)",
    };
    const badgeStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginTop: 16,
        padding: "8px 12px",
        borderRadius: 999,
        border: "1px solid rgba(0,255,255,0.28)",
        background: "rgba(0,255,255,0.08)",
        color: "#87f7ff",
        fontSize: 12,
    };
    const buttonRow = {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 18,
    };
    const linkButton = {
        textDecoration: "none",
        color: "#eafcff",
        background: "rgba(0,255,255,0.10)",
        border: "1px solid rgba(0,255,255,0.28)",
        padding: "12px 16px",
        borderRadius: 14,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: isMobileSupport ? "100%" : "auto",
        minWidth: 0,
        textAlign: "center",
    };
    const sectionTitle = {
        margin: "0 0 16px 0",
        fontSize: 22,
        color: "#7cefff",
        letterSpacing: 1,
    };
    const fieldGrid = {
        display: "grid",
        gridTemplateColumns: isMobileSupport ? "1fr" : "repeat(2, minmax(0, 1fr))",
        gap: isMobileSupport ? 12 : 14,
    };
    const fullWidth = {
        gridColumn: "1 / -1",
    };
    const labelStyle = {
        display: "block",
        marginBottom: 8,
        fontSize: 13,
        color: "rgba(234,252,255,0.86)",
        fontWeight: 700,
        letterSpacing: 0.4,
    };
    const inputStyle = {
        width: "100%",
        padding: "14px 14px",
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(5, 11, 22, 0.92)",
        color: "#fff",
        outline: "none",
        boxSizing: "border-box",
        fontFamily: "inherit",
        fontSize: 14,
    };
    const textareaStyle = {
        ...inputStyle,
        minHeight: 180,
        resize: "vertical",
    };
    const submitBtn = {
        marginTop: 16,
        border: "1px solid rgba(0,255,255,0.32)",
        background: "linear-gradient(135deg, rgba(0,255,255,0.18), rgba(255,255,255,0.08))",
        color: "#f3feff",
        borderRadius: 14,
        padding: "13px 18px",
        fontWeight: 800,
        fontSize: 14,
        cursor: sending ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        boxShadow: "0 0 22px rgba(0,255,255,0.16)",
        opacity: sending ? 0.7 : 1,
    };
    const mutedText = {
        fontSize: 13,
        lineHeight: 1.55,
        color: "rgba(234,252,255,0.72)",
    };
    const statCard = {
        border: "1px solid rgba(255,255,255,0.10)",
        background: "linear-gradient(180deg, rgba(8,18,32,0.82), rgba(3,8,16,0.68))",
        borderRadius: 18,
        padding: "16px 16px",
        boxShadow: "0 14px 34px rgba(0,0,0,0.22)",
        marginBottom: 12,
    };
    const statLabel = {
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: "rgba(234,252,255,0.60)",
        marginBottom: 6,
    };
    const statValue = {
        fontSize: 15,
        fontWeight: 700,
        lineHeight: 1.35,
    };
    const successBox = {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: "rgba(0,255,170,0.08)",
        border: "1px solid rgba(0,255,170,0.22)",
        color: "#caffef",
        lineHeight: 1.55,
    };
    const errorBox = {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: "rgba(255,80,80,0.08)",
        border: "1px solid rgba(255,80,80,0.24)",
        color: "#ffd6d6",
        lineHeight: 1.55,
    };
    return (_jsx("div", { style: shellStyle, children: _jsxs("div", { style: pageStyle, children: [_jsxs("div", { style: topGrid, children: [_jsxs("section", { style: heroStyle, children: [_jsx("div", { style: eyebrowStyle, children: "AVAAIO\u2122 Help Center" }), _jsx("h1", { style: titleStyle, children: "HELP / SUPPORT" }), _jsx("div", { style: subStyle, children: "Get help with your AVAAIO\u2122 account, billing, login access, subscriptions, technical issues, feature requests, and platform guidance. Ava Support is here to help keep your experience smooth, secure, and connected." }), _jsxs("div", { style: { marginTop: 12, fontSize: 14, color: "#7cefff" }, children: ["Priority Support Email: ", _jsx("strong", { children: "support@avaaio.com" })] }), _jsxs("div", { style: badgeStyle, children: [_jsx("span", { children: "\u25CF" }), _jsx("span", { children: token ? "Secure signed-in support session" : "Guest support session" })] })] }), _jsxs("aside", { style: sideStyle, children: [_jsx("h2", { style: sectionTitle, children: "Support Overview" }), _jsxs("div", { style: statCard, children: [_jsx("div", { style: statLabel, children: "Support Channel" }), _jsx("div", { style: statValue, children: "AVAAIO Support Desk" })] }), _jsxs("div", { style: statCard, children: [_jsx("div", { style: statLabel, children: "Recommended Support Email" }), _jsx("div", { style: statValue, children: "support@avaaio.com" })] }), _jsxs("div", { style: statCard, children: [_jsx("div", { style: statLabel, children: "User State" }), _jsx("div", { style: statValue, children: token ? "Authenticated user" : "Guest user" })] }), _jsxs("div", { style: mutedText, children: ["You can also contact Ava Support directly at:", _jsx("br", {}), _jsx("br", {}), _jsx("span", { style: { color: "#19e6ff", fontWeight: 700 }, children: "support@avaaio.com" }), _jsx("br", {}), _jsx("br", {}), "Every request is reviewed carefully. Include details like screenshots, account email, device, browser, and steps to reproduce when possible."] })] })] }), _jsxs("div", { style: bodyGrid, children: [_jsxs("section", { style: formCard, children: [_jsx("h2", { style: sectionTitle, children: "Contact Support" }), _jsx("div", { style: { ...mutedText, marginBottom: 18 }, children: "Send a secure request to the AVAAIO\u2122 support team. The more detail you include, the faster we can understand and route your issue." }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { style: fieldGrid, children: [_jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Name" }), _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "Your name", style: inputStyle, required: true })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Email" }), _jsx("input", { type: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: "Your email", style: inputStyle, required: true })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Category" }), _jsxs("select", { value: category, onChange: (e) => setCategory(e.target.value), style: inputStyle, children: [_jsx("option", { value: "general", children: "General" }), _jsx("option", { value: "billing", children: "Billing" }), _jsx("option", { value: "technical", children: "Technical" }), _jsx("option", { value: "account", children: "Account" }), _jsx("option", { value: "feature", children: "Feature Request" }), _jsx("option", { value: "other", children: "Other" })] })] }), _jsxs("div", { children: [_jsx("label", { style: labelStyle, children: "Subject" }), _jsx("input", { type: "text", value: subject, onChange: (e) => setSubject(e.target.value), placeholder: "How can we help?", style: inputStyle, required: true })] }), _jsxs("div", { style: fullWidth, children: [_jsx("label", { style: labelStyle, children: "Message" }), _jsx("textarea", { value: message, onChange: (e) => setMessage(e.target.value), placeholder: "Describe what happened, what you expected, and any steps we can use to reproduce the issue...", style: textareaStyle, required: true })] }), _jsx("div", { style: fullWidth, children: _jsxs("label", { style: {
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 10,
                                                            fontSize: 13,
                                                            color: "rgba(234,252,255,0.82)",
                                                        }, children: [_jsx("input", { type: "checkbox", checked: includeAccountContext, onChange: (e) => setIncludeAccountContext(e.target.checked) }), "Include account/session context with this support request"] }) })] }), _jsx("button", { type: "submit", style: submitBtn, disabled: sending, children: sending ? "Sending Support Request..." : "Send Support Request" })] }), errorText ? _jsx("div", { style: errorBox, children: errorText }) : null, submitted ? (_jsxs("div", { style: successBox, children: ["Your support request has been sent successfully.", _jsx("br", {}), _jsx("br", {}), "A copy has been routed to:", _jsx("br", {}), _jsx("span", { style: { color: "#19e6ff", fontWeight: 700 }, children: "support@avaaio.com" }), _jsx("br", {}), _jsx("br", {}), "Our support team will review your request and respond as soon as possible."] })) : null] }), _jsxs("section", { style: infoCard, children: [_jsx("h2", { style: sectionTitle, children: "Support Categories" }), _jsx("div", { style: { ...mutedText, marginBottom: 14 }, children: "Choose the category that best matches your request so support can route it properly." }), _jsxs("div", { style: statCard, children: [_jsx("div", { style: statLabel, children: "Account Help" }), _jsx("div", { style: statValue, children: "Login issues, password reset, verification, account recovery" })] }), _jsxs("div", { style: statCard, children: [_jsx("div", { style: statLabel, children: "Billing" }), _jsx("div", { style: statValue, children: "Memberships, subscriptions, renewals, cancellations, Apple billing" })] }), _jsx("div", { style: statCard, children: _jsx("div", { style: statLabel, children: "Technical" }) }), _jsxs("div", { style: statCard, children: [_jsx("div", { style: statLabel, children: "Feature Requests" }), _jsx("div", { style: statValue, children: "Ideas for Ava, education tools, games, workflow help, and new features" })] }), _jsxs("div", { style: mutedText, children: ["Users can reach support directly at", " ", _jsx("span", { style: { color: "#19e6ff", fontWeight: 700 }, children: "support@avaaio.com" }), ". Please allow some time for our support team to review incoming requests. We appreciate your patience."] })] })] })] }) }));
}
